import { useCallback, useEffect, useRef, useState } from "react";
import {
  createMessageTemplate,
  deleteMessageTemplate,
  getMergeFields,
  getMessageTemplates,
  updateMessageTemplate,
} from "../../services/messaging";
import type { MergeField, MessagingTemplate } from "../../types/messaging";
import { renderTemplatePreview, SAMPLE_MERGE_VALUES } from "./mergeFields";
import "./MessageTemplatesPage.css";

/**
 * Authoring surface for SMS templates.
 *
 * The preview here is illustrative — it fills every token from sample data so
 * the coach can read the shape of the message. The authoritative resolution,
 * including which recipients are missing data, happens server-side in the bulk
 * send preview; nothing here decides whether a message can actually go out.
 */
export const MessageTemplatesPage = () => {
  const [templates, setTemplates] = useState<MessagingTemplate[]>([]);
  const [mergeFields, setMergeFields] = useState<MergeField[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formName, setFormName] = useState("");
  const [formBody, setFormBody] = useState("");
  const [formShared, setFormShared] = useState(false);
  const [saving, setSaving] = useState(false);

  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [loadedTemplates, loadedFields] = await Promise.all([
        getMessageTemplates(),
        getMergeFields(),
      ]);
      setTemplates(loadedTemplates);
      setMergeFields(loadedFields);
      setError(null);
    } catch {
      setError("Failed to load templates.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openNew = () => {
    setEditingId(null);
    setFormName("");
    setFormBody("");
    setFormShared(false);
    setError(null);
    setShowForm(true);
  };

  const openEdit = (template: MessagingTemplate) => {
    setEditingId(template.id);
    setFormName(template.name);
    setFormBody(template.body);
    setFormShared(template.isShared);
    setError(null);
    setShowForm(true);
  };

  /** Inserts a token at the cursor rather than appending, so it lands mid-sentence. */
  const insertField = (key: string) => {
    const textarea = bodyRef.current;
    const token = `{{${key}}}`;

    if (!textarea) {
      setFormBody((body) => body + token);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    setFormBody((body) => body.slice(0, start) + token + body.slice(end));

    // Restore the caret after React re-renders the controlled value.
    requestAnimationFrame(() => {
      textarea.focus();
      const caret = start + token.length;
      textarea.setSelectionRange(caret, caret);
    });
  };

  const handleSave = async () => {
    if (!formName.trim() || !formBody.trim()) {
      setError("Name and message are required.");
      return;
    }

    setSaving(true);
    try {
      const request = {
        name: formName.trim(),
        body: formBody,
        isShared: formShared,
      };

      if (editingId) {
        await updateMessageTemplate(editingId, request);
      } else {
        await createMessageTemplate(request);
      }

      setShowForm(false);
      setEditingId(null);
      await load();
    } catch (err) {
      // The API rejects unknown merge tokens by name — surfacing its message
      // tells the coach which token they mistyped.
      setError(
        err instanceof Error ? err.message : "Failed to save the template."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (template: MessagingTemplate) => {
    if (!window.confirm(`Delete "${template.name}"?`)) return;

    try {
      await deleteMessageTemplate(template.id);
      await load();
    } catch {
      setError("Failed to delete the template.");
    }
  };

  const previewBody = renderTemplatePreview(formBody);

  return (
    <div className="message-templates-page">
      <div className="message-templates-page__header">
        <h2>Message templates</h2>
        {!showForm && (
          <button
            type="button"
            className="message-templates-page__new-btn"
            onClick={openNew}
          >
            New template
          </button>
        )}
      </div>

      {error && <div className="message-templates-page__error">{error}</div>}

      {showForm && (
        <div className="message-templates-page__form">
          <div className="message-templates-page__form-field">
            <label htmlFor="template-name">Name</label>
            <input
              id="template-name"
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Camp invite"
              maxLength={120}
            />
          </div>

          <div className="message-templates-page__form-field">
            <label htmlFor="template-body">Message</label>
            <textarea
              id="template-body"
              ref={bodyRef}
              value={formBody}
              onChange={(e) => setFormBody(e.target.value)}
              rows={5}
              maxLength={1600}
              placeholder="Hi {{firstName}}, we'd love to see you at camp."
            />
            <div className="message-templates-page__char-count">
              {formBody.length}/1600
            </div>
          </div>

          <div className="message-templates-page__form-field">
            {/* Not a <label>: these are insert buttons, not a form control, so
                there is nothing for a label to point at. A named group gives
                screen readers the same heading without the false association. */}
            <span
              className="message-templates-page__group-label"
              id="merge-fields-label"
            >
              Merge fields
            </span>
            <div
              className="message-templates-page__fields"
              role="group"
              aria-labelledby="merge-fields-label"
            >
              {mergeFields.map((field) => (
                <button
                  key={field.key}
                  type="button"
                  className="message-templates-page__field-chip"
                  onClick={() => insertField(field.key)}
                  title={`Available on: ${field.supportedOn.join(", ")}`}
                >
                  {field.label}
                </button>
              ))}
            </div>
            <p className="message-templates-page__hint">
              A recipient missing one of these blocks the send rather than
              receiving a blank — you'll see exactly who before anything goes
              out.
            </p>
          </div>

          {formBody.trim() && (
            <div className="message-templates-page__preview">
              <span className="message-templates-page__preview-label">
                Sample preview
              </span>
              <p>{previewBody}</p>
            </div>
          )}

          <label className="message-templates-page__checkbox">
            <input
              type="checkbox"
              checked={formShared}
              onChange={(e) => setFormShared(e.target.checked)}
            />
            Share with the whole staff
          </label>

          <div className="message-templates-page__form-actions">
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setError(null);
              }}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="message-templates-page__save-btn"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="message-templates-page__loading">Loading…</div>
      ) : templates.length === 0 ? (
        <div className="message-templates-page__empty">
          No templates yet. Create one to stop retyping the same message with
          the name swapped.
        </div>
      ) : (
        <ul className="message-templates-page__list">
          {templates.map((template) => (
            <li key={template.id} className="message-templates-page__item">
              <div className="message-templates-page__item-main">
                <div className="message-templates-page__item-name">
                  {template.name}
                  {template.isShared && (
                    <span className="message-templates-page__badge">
                      Shared
                    </span>
                  )}
                </div>
                <p className="message-templates-page__item-body">
                  {template.body}
                </p>
                {template.mergeFields.length > 0 && (
                  <div className="message-templates-page__item-fields">
                    {template.mergeFields.map((field) => (
                      <span key={field}>{field}</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="message-templates-page__item-actions">
                <button type="button" onClick={() => openEdit(template)}>
                  Edit
                </button>
                <button
                  type="button"
                  className="message-templates-page__delete"
                  onClick={() => handleDelete(template)}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="message-templates-page__footnote">
        Sample values used in the preview:{" "}
        {Object.entries(SAMPLE_MERGE_VALUES)
          .slice(0, 4)
          .map(([key, value]) => `${key} = ${value}`)
          .join(", ")}
        .
      </p>
    </div>
  );
};
